/**
 * Configurações > Conta > Integrações de IA (ADR-0091).
 *
 * Tudo da própria pessoa: o endereço do MCP do ERP com o passo a passo de cada
 * IA, os aplicativos conectados por OAuth (com "Desconectar"), as chaves
 * pessoais (o valor aparece UMA vez, ao gerar) e o que as IAs fizeram na
 * agenda nos últimos 30 dias, com "Desfazer" enquanto vale (24 h, ninguém
 * mexeu depois). Gerar chave pede verificação em duas etapas recente: o
 * step-up do AuthContext abre sozinho quando o backend pede.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import AsyncButton from "../../../components/AsyncButton";
import BotaoCopiar from "../../../components/BotaoCopiar";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { useAuth } from "../../../context/AuthContext";
import {
  criarChave,
  desconectarAplicativo,
  desfazerAcaoDaIa,
  lerAtividadeDaIa,
  lerMinhasIntegracoes,
  revogarChave,
} from "../../../services/conexoesIa";
import AtalhoDoGuia from "../conexoes/AtalhoDoGuia";
import { DICA_DOS_MENUS, PASSOS_POR_IA, ehComando } from "../conexoes/passos";
import "./IntegracoesIA.css";

const formatoData = new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric" });
const formatoInstante = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
});
const data = (iso) => (iso ? formatoData.format(new Date(iso)) : "");
const instante = (iso) => (iso ? formatoInstante.format(new Date(iso)) : "");
const ultimoUso = (iso) => (iso ? `último uso ${instante(iso)}` : "nunca usada");

function Secao({ titulo, descricao, children }) {
  return (
    <section className="set-section">
      <div className="set-section__head">
        <h2 className="set-section__title">{titulo}</h2>
        {descricao ? <p className="set-section__desc">{descricao}</p> : null}
      </div>
      <div className="set-section__body">{children}</div>
    </section>
  );
}

function Escopos({ itens }) {
  return (
    <span className="ia-escopos">
      {(itens || []).map((e) => <span key={e.codigo} className="ia-escopo">{e.rotulo}</span>)}
    </span>
  );
}

function Conectar({ endereco }) {
  const [aba, setAba] = useState(PASSOS_POR_IA[0].id);
  const atual = PASSOS_POR_IA.find((p) => p.id === aba);
  return (
    <>
      <div className="ia-endereco">
        <code title={endereco}>{endereco}</code>
        <BotaoCopiar texto={endereco} rotulo="Copiar URL" aria-label="Copiar o endereço do MCP" />
      </div>
      <div className="ia-abas" role="tablist" aria-label="Como conectar cada IA">
        {PASSOS_POR_IA.map((p) => (
          <button key={p.id} type="button" role="tab" aria-selected={p.id === aba} className={p.id === aba ? "ativa" : ""} onClick={() => setAba(p.id)}>
            {p.nome}
          </button>
        ))}
      </div>
      <p key={`r-${aba}`} className="ia-requisito">{atual.requisito}</p>
      <ol key={aba} className="ia-passos" role="tabpanel">
        {atual.passos.map((passo) => {
          const texto = passo.replace("{url}", endereco);
          return ehComando(passo) ? (
            <li key={passo} className="ia-comando"><code>{texto}</code><BotaoCopiar texto={texto} aria-label="Copiar o comando" /></li>
          ) : <li key={passo}>{texto}</li>;
        })}
      </ol>
      <p className="ia-dica">{DICA_DOS_MENUS}</p>
    </>
  );
}

function NovaChave({ possiveis, validades, padrao, onCriada }) {
  const { request } = useAuth();
  const [nome, setNome] = useState("");
  const [escopos, setEscopos] = useState(() => possiveis.map((e) => e.codigo));
  const [dias, setDias] = useState(padrao);
  const alternar = (codigo) => setEscopos((lista) => (lista.includes(codigo) ? lista.filter((c) => c !== codigo) : [...lista, codigo]));
  const pronto = nome.trim() && escopos.length;

  const gerar = async () => {
    const resposta = await criarChave(request, { nome: nome.trim(), escopos, dias });
    setNome("");
    onCriada(resposta);
  };

  return (
    <div className="ia-nova">
      <label className="ia-campo ia-nome">
        <span>Nome</span>
        <input className="input" value={nome} maxLength={80} placeholder="Ex.: Claude Code no notebook" onChange={(e) => setNome(e.target.value)} />
      </label>
      <fieldset className="ia-campo ia-checks">
        <legend>O que a chave pode</legend>
        {possiveis.map((e) => (
          <label key={e.codigo} title={e.descricao}>
            <input type="checkbox" checked={escopos.includes(e.codigo)} onChange={() => alternar(e.codigo)} />
            {e.rotulo}
          </label>
        ))}
      </fieldset>
      <label className="ia-campo ia-validade">
        <span>Validade</span>
        <select className="input" value={dias} onChange={(e) => setDias(Number(e.target.value))}>
          {validades.map((d) => <option key={d} value={d}>{d === 365 ? "1 ano" : `${d} dias`}</option>)}
        </select>
      </label>
      <AsyncButton variant="primary" disabled={!pronto} onClick={gerar} silentSuccess>Gerar chave</AsyncButton>
    </div>
  );
}

function ChaveRevelada({ valor, onFechar }) {
  return (
    <div className="ia-revelada" role="status">
      <b>Copie a chave agora: ela não aparece de novo.</b>
      <div className="ia-endereco">
        <code title={valor}>{valor}</code>
        <BotaoCopiar texto={valor} aria-label="Copiar a chave" />
      </div>
      <button type="button" className="btn btn--ghost btn--sm" onClick={onFechar}>Já copiei</button>
    </div>
  );
}

const ATIVIDADE_POR_PAGINA = 10;

/** A atividade chega 10 por vez; "Mostrar mais" pede a próxima página ao servidor. */
function AtividadeDaIa({ request, primeiraPagina, dias }) {
  const [itens, setItens] = useState(primeiraPagina.itens);
  const [temMais, setTemMais] = useState(primeiraPagina.temMais);
  useEffect(() => {
    setItens(primeiraPagina.itens);
    setTemMais(primeiraPagina.temMais);
  }, [primeiraPagina.itens, primeiraPagina.temMais]);

  const mostrarMais = async () => {
    const pagina = await lerAtividadeDaIa(request, { inicio: itens.length, quantos: ATIVIDADE_POR_PAGINA });
    setItens((atuais) => {
      // Uma ação nova da IA entre as páginas empurra a lista: sem isto, um item repetiria.
      const vistos = new Set(atuais.map((a) => a.id));
      return [...atuais, ...pagina.itens.filter((a) => !vistos.has(a.id))];
    });
    setTemMais(pagina.tem_mais);
  };
  // Desfazer muda o estado de mais de um item (o "pode desfazer" dos vizinhos):
  // relê o que já está aberto, sem fechar as páginas que a pessoa abriu.
  const desfazer = async (id) => {
    await desfazerAcaoDaIa(request, id);
    const pagina = await lerAtividadeDaIa(request, { inicio: 0, quantos: Math.max(itens.length, ATIVIDADE_POR_PAGINA) });
    setItens(pagina.itens);
    setTemMais(pagina.tem_mais);
  };

  if (!itens.length) return <p className="ia-vazio">Nenhuma ação de IA nos últimos {dias} dias.</p>;
  return (
    <>
      <ul className="ia-lista">
        {itens.map((a) => (
          <li key={a.id} className={`ia-item${a.desfeito ? " desfeito" : ""}`}>
            <div className="ia-item-t">
              <span className="ia-acao"><span className="ia-selo">{a.ia}</span>{a.texto}</span>
              <small>
                <Link to={`/minhas-pendencias?tarefa=${a.tarefa.id}`} title={a.tarefa.titulo}>{a.tarefa.titulo}</Link>
                {` · ${instante(a.quando)}${a.desfeito ? " · desfeito" : ""}`}
              </small>
            </div>
            {a.pode_desfazer ? (
              <AsyncButton variant="secondary" successMessage="Ação desfeita." onClick={() => desfazer(a.id)}>
                Desfazer
              </AsyncButton>
            ) : null}
          </li>
        ))}
      </ul>
      {temMais ? (
        <div className="ia-mais">
          <AsyncButton variant="secondary" silentSuccess onClick={mostrarMais}>Mostrar mais</AsyncButton>
        </div>
      ) : null}
    </>
  );
}

export default function IntegracoesIATab() {
  const { request } = useAuth();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");
  const [revelada, setRevelada] = useState("");
  const [confirmar, setConfirmar] = useState(null);

  const carregar = useCallback(() => lerMinhasIntegracoes(request)
    .then((r) => { setDados(r); setErro(""); })
    .catch((e) => setErro(e?.message || "Não deu para carregar as integrações.")), [request]);
  useEffect(() => { carregar(); }, [carregar]);

  if (erro) return <Secao titulo="Integrações de IA"><p className="ia-erro">{erro}</p></Secao>;
  if (!dados) return <Secao titulo="Integrações de IA"><div aria-busy="true" className="ia-carregando">Carregando…</div></Secao>;

  const semAcesso = !dados.escopos_possiveis.length;
  const executarConfirmacao = async () => {
    await confirmar.acao();
    setConfirmar(null);
    await carregar();
  };

  return (
    <div className="ia-tela">
      <Secao
        titulo="Conectar uma IA"
        descricao="Claude, ChatGPT e outras IAs leem e mexem na sua agenda em seu nome, com as mesmas regras da tela. Tudo o que fizerem fica no histórico com o nome da IA e pode ser desfeito em até 24 h."
      >
        {semAcesso ? (
          <p className="ia-aviso">A agenda ainda não está liberada para você, então não há o que conectar por enquanto.</p>
        ) : (
          <>
            <AtalhoDoGuia caminho="ia" texto="Primeira vez? O guia mostra cada clique, IA por IA." />
            <Conectar endereco={dados.endereco_mcp} />
          </>
        )}
      </Secao>

      <Secao titulo="Aplicativos conectados" descricao="IAs que você autorizou pelo login do ERP.">
        {dados.conexoes.length ? (
          <ul className="ia-lista">
            {dados.conexoes.map((c) => (
              <li key={`${c.cliente_id}-${c.recurso}`} className="ia-item">
                <div className="ia-item-t">
                  <b>{c.aplicativo}</b>
                  <small>{`${c.recurso_nome} · ${ultimoUso(c.ultimo_uso_em)}`}</small>
                  <Escopos itens={c.escopos} />
                </div>
                <button
                  type="button" className="btn btn--ghost btn--sm ia-perigo"
                  onClick={() => setConfirmar({
                    titulo: `Desconectar ${c.aplicativo}?`,
                    descricao: "O acesso para na próxima chamada. Para voltar, é preciso conectar de novo pela IA.",
                    rotulo: "Desconectar",
                    acao: () => desconectarAplicativo(request, c),
                  })}
                >
                  Desconectar
                </button>
              </li>
            ))}
          </ul>
        ) : <p className="ia-vazio">Nenhuma IA conectada pelo login do ERP.</p>}
      </Secao>

      <Secao titulo="Chaves pessoais" descricao="Para IAs que não fazem o login pelo ERP (Claude Code, scripts). Guardamos só um resumo da chave: perdeu, revogue e gere outra.">
        {revelada ? <ChaveRevelada valor={revelada} onFechar={() => setRevelada("")} /> : null}
        {dados.chaves.length ? (
          <ul className="ia-lista">
            {dados.chaves.map((k) => (
              <li key={k.id} className={`ia-item${k.vencida ? " vencida" : ""}`}>
                <div className="ia-item-t">
                  <b>{k.nome}</b>
                  <small>
                    <code>{`${k.prefixo}…`}</code>
                    {` · ${k.vencida ? "venceu" : "vence"} em ${data(k.expira_em)} · ${ultimoUso(k.ultimo_uso_em)}`}
                  </small>
                  <Escopos itens={k.escopos} />
                </div>
                <button
                  type="button" className="btn btn--ghost btn--sm ia-perigo"
                  onClick={() => setConfirmar({
                    titulo: `Revogar a chave “${k.nome}”?`,
                    descricao: "Quem usa esta chave perde o acesso na próxima chamada. Não dá para desfazer.",
                    rotulo: "Revogar",
                    acao: () => revogarChave(request, k.id),
                  })}
                >
                  Revogar
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {semAcesso ? null : (
          <NovaChave
            possiveis={dados.escopos_possiveis}
            validades={dados.validades}
            padrao={dados.validade_padrao}
            onCriada={(r) => { setRevelada(r.valor); carregar(); }}
          />
        )}
      </Secao>

      <Secao titulo="Atividade da IA" descricao={`O que as IAs fizeram na sua agenda nos últimos ${dados.dias_de_atividade} dias.`}>
        <AtividadeDaIa
          request={request}
          primeiraPagina={{ itens: dados.atividade, temMais: Boolean(dados.atividade_tem_mais) }}
          dias={dados.dias_de_atividade}
        />
      </Secao>

      <ConfirmDialog
        open={Boolean(confirmar)}
        titulo={confirmar?.titulo}
        descricao={confirmar?.descricao}
        confirmarLabel={confirmar?.rotulo}
        tone="danger"
        onConfirmar={executarConfirmacao}
        onCancelar={() => setConfirmar(null)}
      />
    </div>
  );
}
